import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, UserPlus2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isSalesManagerRole } from "@/lib/roleAliases";

export default function AddLeadModal({ open, onClose, onSaved }) {
  const { isAdmin, roleName } = useAuth();
  const [executives, setExecutives] = useState([]);
  const [form, setForm] = useState({ client_name: "", phone: "", email: "", source: "", notes: "", assigned_to_user_id: "", lead_date: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setForm({ client_name: "", phone: "", email: "", source: "", notes: "", assigned_to_user_id: "", lead_date: "" });
    (async () => {
      try {
        const { data } = await api.get("/sales/executives");
        setExecutives(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.phone) { setErr("Client name and phone are required"); return; }
    setSaving(true);
    try {
      const { data: created } = await api.post("/sales/leads", {
        ...form,
        assigned_to_user_id: form.assigned_to_user_id || null,
        lead_date: form.lead_date || null,
      });
      toast.success(form.assigned_to_user_id ? "Lead assigned" : "Lead added");
      onSaved?.(created);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="add-lead-modal">
      <div className="w-full max-w-lg iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
            <UserPlus2 className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Ingest</p>
            <h2 className="font-display text-2xl text-white">Add new lead</h2>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Client Name</label>
            <input value={form.client_name} onChange={(e) => setForm(f => ({...f, client_name: e.target.value}))} className="iz-input" required data-testid="lead-client-name"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Phone</label>
              <input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} className="iz-input" required data-testid="lead-client-phone"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} className="iz-input" data-testid="lead-client-email"/>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Source</label>
            <input value={form.source} onChange={(e) => setForm(f => ({...f, source: e.target.value}))} className="iz-input" placeholder="e.g. Referral, Website, Field" data-testid="lead-source"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} className="iz-input" rows={2}/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
              Lead Date <span className="text-gray-600 lowercase">· optional</span>
            </label>
            <input
              type="date"
              value={form.lead_date}
              onChange={(e) => setForm(f => ({...f, lead_date: e.target.value}))}
              max={new Date().toISOString().slice(0, 10)}
              className="iz-input"
              data-testid="lead-date-input"
            />
            <p className="text-[10px] text-gray-500 mt-1 font-mono">Leave blank to use today. Backdate to file the lead into an earlier month.</p>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Route To</label>
            <select value={form.assigned_to_user_id} onChange={(e) => setForm(f => ({...f, assigned_to_user_id: e.target.value}))} className="iz-input" data-testid="lead-route-select">
              <option value="">Unassigned (available to team)</option>
              {isSalesManagerRole(roleName) && <option value="__self__" data-testid="route-self">Assign to Self</option>}
              {executives.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <p className="text-[10px] text-gray-500 mt-2 font-mono">If unassigned, executives can claim it from the Lead Ledger.</p>
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="lead-submit">
              {saving ? "Saving…" : "Add Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
